import { GridRowParams } from '@mui/x-data-grid-pro';
import { createContext, useMemo, useRef, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { TeamsIcon } from '@/components/icons/TeamsIcon';
import { Users01Icon } from '@/components/icons/Users01Icon';
import { UserSettingsIcon } from '@/components/icons/UserSettingsIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { SearchFilterToolbar } from '@/components/tables/DataTable/components/SearchFilterToolbar';
import { PageSizes } from '@/components/tables/DataTable/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import {
  PaginatedTableProps,
  usePaginatedDataTableQuery,
} from '@/components/tables/DataTable/hooks/usePaginatedDataTableQuery';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import {
  StaticFilterGroup,
  TableFilterProps,
} from '@/components/tables/TableFilter/types';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { EMPLOYEE_TIER_DISPLAY_MAP } from '@/modules/employees/constants';
import { EmployeeModal } from '@/modules/employees/EmployeeModal/EmployeeModal';
import { ROUTE_KEYS } from '@/navigation/constants';
import { GuardedPageRedirect } from '@/navigation/GuardedPageRedirect';
import {
  getAdminUsersWhere,
  mapDataToRows,
  useUserTableColumns,
} from '@/pages/admin/AdminUsersPage/AdminUsersPage.utils';
import { EmployeeTier } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  useGetAdminUserFilterOptionsQuery,
  useGetAdminUsersQuery,
} from './graphql/AdminUsers.generated';

type Row = ReturnType<typeof mapDataToRows>[number];

const UserFiltersContext = createContext<TableFilterProps | null>(null);

function AdminUsersSearchFilterToolbar() {
  const tableFilterProps = useGuardedContext(
    UserFiltersContext,
    'UserFiltersContext'
  );
  return (
    <SearchFilterToolbar
      searchProps={{ placeholder: 'Search users' }}
      tableFilterProps={tableFilterProps}
    />
  );
}

export interface Props {
  rows: Row[];
  loading: boolean;
  paginatedTableProps: PaginatedTableProps;
  onSearchChange: (search: string) => void;
}

const slots = {
  toolbar: AdminUsersSearchFilterToolbar,
  noRowsOverlay: () => <TableEmptyState text="No users" />,
};

function AdminUsersPageInner({
  rows,
  loading,
  paginatedTableProps,
  onSearchChange,
}: Props) {
  const columns = useUserTableColumns();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState<null | string>(null);
  const handleAddNewUserClick = () => {
    setIsModalOpen(true);
    setEmployeeId(null);
  };

  return (
    <SubpageLayout
      heading="All internal users"
      actions={
        <Button
          size="sm"
          variant="secondary"
          startIcon={PlusIcon}
          onClick={handleAddNewUserClick}
        >
          Add user
        </Button>
      }
    >
      {isModalOpen && (
        <EmployeeModal
          isOpen
          closeHandler={() => setIsModalOpen(false)}
          employeeId={employeeId}
        />
      )}
      <DataTableCard>
        <DataTable
          {...paginatedTableProps}
          alwaysShowPaginationFooter
          columns={columns}
          rows={rows}
          loading={loading}
          slots={slots}
          filterMode="server"
          onFilterModelChange={({ quickFilterValues }) =>
            onSearchChange((quickFilterValues ?? []).join(' '))
          }
          onRowClick={(row: GridRowParams<Row>) => {
            setIsModalOpen(true);
            setEmployeeId(row.row.id);
          }}
        />
      </DataTableCard>
    </SubpageLayout>
  );
}

function AdminUsersPageContent() {
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const [search, setSearch] = useState('');
  const [filterValue, setFilterValue] = useState<Record<string, string[]>>({});
  const pendingFilterChange = useRef(false);
  const roleIds = filterValue.role ?? [];
  const tiers = Object.values(EmployeeTier).filter((tier) =>
    filterValue.type?.includes(tier)
  );
  const teamIds = filterValue.team ?? [];
  const { data: filterOptions, error: optionsError } =
    useGetAdminUserFilterOptionsQuery({
      onError: () =>
        showFeedback(
          'We could not load role and team filters. Please refresh the page and try again.'
        ),
    });

  const [paginatedTableProps, { data, loading, error }, { resetPagination }] =
    usePaginatedDataTableQuery(useGetAdminUsersQuery, {
      pageSize: PageSizes.Thirty,
      variables: {
        where: getAdminUsersWhere({
          search,
          roleIds,
          tiers,
          teamIds,
        }),
      },
      onCompleted: () => {
        if (pendingFilterChange.current) {
          pendingFilterChange.current = false;
          trackUserEvent('user list_filtered', {
            hasSearch: Boolean(search.trim()),
            roleCount: roleIds.length,
            typeCount: tiers.length,
            teamCount: teamIds.length,
          });
        }
      },
      onError: () => {
        pendingFilterChange.current = false;
        showFeedback(
          "We weren't able to load users. Please refresh the page and try again."
        );
      },
    });

  const handleFiltersChange = (value: Record<string, string[]>) => {
    pendingFilterChange.current = true;
    resetPagination();
    setFilterValue(value);
  };

  const groups: StaticFilterGroup[] = [
    {
      id: 'role',
      label: 'Role',
      icon: <UserSettingsIcon size={16} />,
      multi: true,
      options: getNodes(filterOptions?.roles).map((role) => ({
        value: role.id,
        label: role.displayName,
      })),
    },
    {
      id: 'type',
      label: 'Type',
      icon: <Users01Icon size={16} />,
      multi: true,
      searchable: false,
      options: Object.values(EmployeeTier).map((value) => ({
        value,
        label: EMPLOYEE_TIER_DISPLAY_MAP[value],
      })),
    },
    {
      id: 'team',
      label: 'Team',
      icon: <TeamsIcon size={16} />,
      multi: true,
      options: getNodes(filterOptions?.teams).map((team) => ({
        value: team.id,
        label: team.name,
      })),
    },
  ];

  useReportError('failed to load data for users page', error);
  useReportError('failed to load user filter options', optionsError);

  const rows = useMemo(() => mapDataToRows(data), [data]);

  return (
    <UserFiltersContext.Provider
      value={{
        groups,
        value: filterValue,
        onChange: handleFiltersChange,
      }}
    >
      <AdminUsersPageInner
        rows={rows}
        loading={loading}
        paginatedTableProps={paginatedTableProps}
        onSearchChange={(value) => {
          if (value === search) return;
          pendingFilterChange.current = true;
          resetPagination();
          setSearch(value);
        }}
      />
    </UserFiltersContext.Provider>
  );
}

export function AdminUsersPage() {
  return (
    <GuardedPageRedirect route={ROUTE_KEYS.ADMIN_USERS}>
      <AdminUsersPageContent />
    </GuardedPageRedirect>
  );
}
